/**
 * Super-admin-only surface for assigning existing enabled modules to the active
 * team. Lists the team's currently-assigned Core/Account modules with a remove
 * action, and an "Add Module" picker of assignable, not-yet-assigned modules.
 * All writes go through the bulk accounts.setTeamModules procedure; an empty
 * assigned set means the team has access to no shared modules (deny-by-default).
 */
import { Plus, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { useAuthStore } from "@/stores/auth";


/**
 * Assign/remove existing modules for the active team. Renders nothing for
 * non-super-admin users (the underlying procedures are super-admin only).
 */
export function ModuleAssignmentSection() {
	const { activeTeam, user } = useAuthStore();
	const isSuperAdmin = user?.role === "super_admin";
	const utils = trpc.useUtils();

	const [pickerOpen, setPickerOpen] = useState(false);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

	const modulesQuery = trpc.accounts.getTeamModules.useQuery(
		{ teamId: activeTeam?.id ?? "" },
		{ enabled: isSuperAdmin && !!activeTeam }
	);

	const setModulesMutation = trpc.accounts.setTeamModules.useMutation({
		onSuccess: () => {
			void utils.accounts.getTeamModules.invalidate();
			void utils.modules.list.invalidate();
			setPickerOpen(false);
			setSelectedIds(new Set());
		},
	});

	if (!isSuperAdmin || !activeTeam) {
		return null;
	}

	const allModules = modulesQuery.data?.modules ?? [];
	const assigned = allModules.filter((m) => m.hasAccess);
	const assignable = allModules.filter((m) => !m.hasAccess);
	const assignedIds = assigned.map((m) => m.id);
	const isBusy = setModulesMutation.isPending;

	/**
	 * Persist the given complete access list for the team.
	 * @param moduleIds - the exact set of module ids the team should access
	 */
	const persist = (moduleIds: string[]) => {
		setModulesMutation.mutate({ teamId: activeTeam.id, moduleIds });
	};

	/**
	 * Remove a single module from the team's access list.
	 * @param moduleId - the module to revoke
	 */
	const handleRemove = (moduleId: string) => {
		persist(assignedIds.filter((id) => id !== moduleId));
	};

	/** Assign the modules checked in the picker (union with the current set). */
	const handleAddSelected = () => {
		persist([...assignedIds, ...selectedIds]);
	};

	/**
	 * Toggle a module in the picker's selection.
	 * @param moduleId - the module toggled
	 * @param checked - whether it is now selected
	 */
	const toggleSelected = (moduleId: string, checked: boolean) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (checked) next.add(moduleId);
			else next.delete(moduleId);
			return next;
		});
	};

	/** Open the picker with a cleared selection. */
	const openPicker = () => {
		setSelectedIds(new Set());
		setPickerOpen(true);
	};

	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between space-y-0">
				<div>
					<CardTitle className="text-lg">Assigned Modules</CardTitle>
					<p className="text-sm text-muted-foreground">
						Modules {activeTeam.name} can access
					</p>
				</div>
				<Button onClick={openPicker} disabled={isBusy}>
					<Plus className="mr-2 h-4 w-4" />
					Add Module
				</Button>
			</CardHeader>
			<CardContent>
				{modulesQuery.isLoading ? (
					<div className="flex items-center justify-center py-6">
						<div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent" />
					</div>
				) : modulesQuery.error ? (
					<div className="rounded-md bg-destructive/10 p-3 text-sm text-red-400">
						{modulesQuery.error.message}
					</div>
				) : assigned.length === 0 ? (
					<p className="py-4 text-sm text-muted-foreground">
						No modules assigned. This team sees no shared modules until you add one.
					</p>
				) : (
					<ul className="divide-y divide-border">
						{assigned.map((mod) => (
							<li key={mod.id} className="flex items-center justify-between py-2">
								<div className="flex items-center gap-2">
									<span className="font-medium">{mod.name}</span>
									<span className="text-xs uppercase tracking-wide text-muted-foreground">
										{mod.scope}
									</span>
								</div>
								<Button
									variant="ghost"
									size="icon"
									onClick={() => { handleRemove(mod.id); }}
									disabled={isBusy}
									title="Remove access"
								>
									<X className="h-4 w-4 text-red-400" />
								</Button>
							</li>
						))}
					</ul>
				)}

				{setModulesMutation.isError && (
					<div className="mt-3 rounded-md bg-destructive/10 p-3 text-sm text-red-400">
						{setModulesMutation.error.message}
					</div>
				)}
			</CardContent>

			<Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
				<DialogContent>
					<form onSubmit={(e) => { e.preventDefault(); handleAddSelected(); }}>
						<DialogHeader>
							<DialogTitle>Add Modules to {activeTeam.name}</DialogTitle>
							<DialogDescription>
								Select modules to make visible to this team.
							</DialogDescription>
						</DialogHeader>
						<div className="max-h-80 space-y-3 overflow-y-auto py-2">
							{allModules.length === 0 ? (
								<p className="text-sm text-muted-foreground">
									This account is not entitled to any modules. A platform administrator
									must grant entitlements in Admin → Accounts → Modules before this team
									can be assigned anything.
								</p>
							) : assignable.length === 0 ? (
								<p className="text-sm text-muted-foreground">
									All available modules are already assigned to this team.
								</p>
							) : (
								assignable.map((mod) => (
									<div key={mod.id} className="flex items-center gap-2">
										<Checkbox
											id={`assign-${mod.id}`}
											checked={selectedIds.has(mod.id)}
											onCheckedChange={(checked: boolean) => { toggleSelected(mod.id, checked); }}
										/>
										<Label htmlFor={`assign-${mod.id}`} className="font-normal">
											{mod.name}
											<span className="ml-2 text-xs uppercase tracking-wide text-muted-foreground">
												{mod.scope}
											</span>
										</Label>
									</div>
								))
							)}
						</div>
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => { setPickerOpen(false); }}
							>
								Cancel
							</Button>
							<Button
								type="submit"
								disabled={selectedIds.size === 0 || isBusy}
							>
								{isBusy ? "Adding..." : "Add Selected"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</Card>
	);
}
